import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import MainApi from "../../../../services/mainApi.service";
import { useSalesContract } from "../../../../store/contexts/SalesContractContext";
import { memo, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Table, useFetch } from "@4uhub/lib4uhub";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import { format } from "date-fns";
import { enUS, ptBR } from "date-fns/locale";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { ISalesContractAttachment } from "../../../../models/sales-contract-attachments";
import { approveSalesContractAttachment } from "../../../../services/salesContractAttachment.service";

const translationsPath =
  "page.4uSalesContract.registers.salesContractAttachments.";

const serviceURL =
  process.env.REACT_APP_MAIN + "/api/v1/SalesContractAttachment/";
const service = new MainApi<ISalesContractAttachment>(serviceURL);

export type TRowClick = GridRowParams<ISalesContractAttachment>;

export const SalesContractAttachmentsPage = memo(() => {
  const { onOpen, selectPropId, onRefresh, selectTabName, refresh } =
    useSalesContract();

  const { sendRequest: approve } = useFetch(approveSalesContractAttachment);

  const { id } = useParams();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const translate = (field: string) => t(translationsPath + field);

  const columns: GridColDef<ISalesContractAttachment>[] = [
    {
      field: "title",
      headerName: translate("title_form"),
      renderCell: ({ value }) => (
        <Typography variant="body2">{value}</Typography>
      ),
    },
    {
      field: "description",
      headerName: translate("description"),
      renderCell: ({ value }) => (
        <Typography variant="body2">{value}</Typography>
      ),
    },
    {
      field: "attachmentDate",
      headerName: translate("attachment_date"),
      renderCell: (params) => {
        if (!params.value) return "-";
        const date = new Date(params.value);
        date.setHours(date.getHours() + date.getTimezoneOffset() / 60);
        return format(date, "P", {
          locale: language === "pt-BR" ? ptBR : enUS,
        });
      },
    },
    {
      field: "approvalDate",
      headerName: translate("approval_date"),
      renderCell: (params) => {
        if (!params.value) return "-";
        const date = new Date(params.value);
        date.setHours(date.getHours() + date.getTimezoneOffset() / 60);
        return format(date, "P", {
          locale: language === "pt-BR" ? ptBR : enUS,
        });
      },
    },
  ];

  const handleAdd = () => {
    onOpen();
    selectTabName("attachment");
  };

  const rowClickHandler = useCallback(
    (params: GridRowParams<ISalesContractAttachment>) => {
      selectPropId(params.row.id);
      onOpen();
      selectTabName("attachment");
    },
    [selectPropId, onOpen, selectTabName]
  );

  const onApproveSalesContractAttachment = useCallback(
    async (id: string) => {
      const { success } = await approve(id);
      if (success) {
        onRefresh();
      }
    },
    [approve, onRefresh]
  );

  const props = useMemo(() => {
    return { SalesContractId: id, refresh };
  }, [id, refresh]);

  return (
    <Box sx={{ height: 600, width: "100%" }}>
      <Box height={600}>
        <Table<ISalesContractAttachment>
          searchInput
          showDefaultMacroFilters={false}
          columns={columns}
          service={service}
          onAdd={handleAdd}
          onRowClick={rowClickHandler}
          serviceProps={props}
          title={translate("title")}
          addButtonLabel={translate("add")}
          searchInputPlaceHolder={translate("search")}
          rowActions={(params) => [
            params.row.approvalDate === null
              ? {
                  action: (params) => {
                    onApproveSalesContractAttachment(params.row.id);
                  },
                  icon: <CheckCircleIcon fontSize="small" />,
                  text: `${t(translationsPath + "approve")}`,
                }
              : null,
          ]}
          confirmOptions={{
            title: translate("confirm.title"),
            description: translate("confirm.description"),
            confirmationButtonProps: { variant: "contained" },
          }}
        />
      </Box>
    </Box>
  );
});
